import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from 'store/store';
import { ACCESS_BASE_URL } from 'App';
import {
  useGetCards,
  useActivateCard,
  useSuspendCard,
  useUnsuspendCard,
  useLockCard,
  useUnlockCard,
  useCancelCard,
  useChangeCardPin,
  usePostCardsSpendingControls,
  useGetCardAccounts,
  useGetCardAccountTransactions,
  useTopupCardAccount,
  useIssueCard,
  GetCards200AllOfCardsItem,
  useUpdateCardPhoneNumber,
  PageToken,
  Xmetadata,
  useWithdrawCardAccount,
  usePostCardaccountsFixedLimit
} from '@utx/utx-access-client';
import { useUtxCardOverviewGetCardDetail, useUtxCountriesGetPhoneDailInList } from '@utx/utx-client';

export interface useCardParams {
  cardAccountId: string;
  pageSize?: number;
  status?: string;
  cardEmbossedName?: string;
  cardId?: string;
}

export interface phoneNumberProp {
  countryCode: string;
  mobileNumber: string;
}

export type CardItem = GetCards200AllOfCardsItem;

export const useCard = ({ cardAccountId, pageSize = 5, status, cardEmbossedName, cardId }: useCardParams) => {
  const store = useStore();

  const [actionResponse, setActionResponse] = useState<any>();
  const [actionError, setActionError] = useState<any>();

  const [currentPageToken, setCurrentPageToken] = useState<PageToken>();
  const [pageToken, setPageToken] = useState<{ nextToken?: PageToken; preToken?: PageToken }>({});
  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken ? true : false);
  }, 100);

  const queryCards = useGetCards(
    {
      cardAccountId: cardAccountId,
      pageSize: pageSize,
      pageToken: currentPageToken,
      status: status,
      cardEmbossedName: cardEmbossedName,
      cardId: cardId
    },
    {
      axios: {
        baseURL: ACCESS_BASE_URL
      },
      query: {
        queryKey: ['queryCards', store.authToken, currentPageToken, pageSize, cardAccountId, status, cardEmbossedName, cardId],
        retry: false,
        enabled,
        keepPreviousData: true
      }
    }
  );

  const cards = useMemo(() => {
    const data = queryCards.data?.data.cards;
    return data || [];
  }, [queryCards.data?.data]);

  const isLoadingCards = useMemo(() => {
    return queryCards.isLoading;
  }, [queryCards.isLoading]);

  const validate = (cardId: string) => {
    if (cards.length == 0) {
      return false;
    }
    const targetCard = cards.find((card) => card.id === cardId);
    if (!targetCard) {
      return false;
    }
    return true;
  };

  const lockCardMutate = useLockCard({ axios: { baseURL: ACCESS_BASE_URL } });
  const lockCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    if (!validate(cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    lockCardMutate.mutate(
      {
        data: {
          cardAccountId: cardAccountId,
          cardId: cardId
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const unlockCardMutate = useUnlockCard({ axios: { baseURL: ACCESS_BASE_URL } });
  const unlockCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    if (!validate(cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    unlockCardMutate.mutate(
      {
        data: {
          cardAccountId: cardAccountId,
          cardId: cardId
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const changeCardPinMutate = useChangeCardPin({ axios: { baseURL: ACCESS_BASE_URL } });
  const changeCardPin = (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    if (!validate(cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    changeCardPinMutate.mutate(
      {
        data: {
          cardAccountId: cardAccountId,
          cardId: cardId,
          pin: pin
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const setCardLimitMutate = usePostCardsSpendingControls({ axios: { baseURL: ACCESS_BASE_URL } });
  const setCardLimit = (
    cardId: string,
    atmLimit?: number,
    purchaseLimit?: number,
    dailyLimit?: number,
    singleTransactionLimit?: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    if (!validate(cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    setCardLimitMutate.mutate(
      {
        data: {
          cardAccountId: cardAccountId,
          cardId: cardId,
          dailyAtmLimit: atmLimit,
          dailyPurchaseLimit: purchaseLimit,
          dailyLimit: dailyLimit,
          singleTransactionLimit: singleTransactionLimit
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const issueCardMutate = useIssueCard({ axios: { baseURL: ACCESS_BASE_URL } });
  const issueCard = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    issueCardMutate.mutate(
      {
        data
      },
      {
        onSuccess,
        onError
      }
    );
  };
  const cancelCardMutate = useCancelCard({ axios: { baseURL: ACCESS_BASE_URL } });
  const cancelCard = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    if (!validate(data?.cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    cancelCardMutate.mutate(
      {
        data
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const activateCardMutate = useActivateCard({ axios: { baseURL: ACCESS_BASE_URL } });
  const activateCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    if (!validate(cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    activateCardMutate.mutate(
      {
        data: {
          cardAccountId: cardAccountId,
          cardId: cardId
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };
  const suspendCardMutate = useSuspendCard({ axios: { baseURL: ACCESS_BASE_URL } });
  const suspendCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    if (!validate(cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    suspendCardMutate.mutate(
      {
        data: {
          cardAccountId: cardAccountId,
          cardId: cardId
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const unsuspendCardMutate = useUnsuspendCard({ axios: { baseURL: ACCESS_BASE_URL } });
  const unsuspendCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    if (!validate(cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    unsuspendCardMutate.mutate(
      {
        data: {
          cardAccountId: cardAccountId,
          cardId: cardId
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const queryCardAccounts = useGetCardAccounts(
    {
      cardAccountId: [cardAccountId]
    },
    {
      axios: {
        baseURL: ACCESS_BASE_URL
      },
      query: {
        queryKey: ['queryCardAccounts', store.authToken, cardAccountId],
        retry: false,
        enabled,
        keepPreviousData: true
      }
    }
  );

  const topUpMutate = useTopupCardAccount({ axios: { baseURL: ACCESS_BASE_URL } });
  const topUpCardAccount = (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    topUpMutate.mutate(
      {
        data: {
          accountId,
          cardAccountId,
          amount: amount,
          currency: currency,
          xmetadata: Object.keys(xmetadata).length ? xmetadata : undefined
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const withdrawMutate = useWithdrawCardAccount({ axios: { baseURL: ACCESS_BASE_URL } });
  const withdrawCardAccount = (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    withdrawMutate.mutate(
      {
        data: {
          accountId,
          cardAccountId,
          amount: amount,
          currency: currency,
          xmetadata: Object.keys(xmetadata).length ? xmetadata : undefined
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const updateCardPhoneNumberMutate = useUpdateCardPhoneNumber({ axios: { baseURL: ACCESS_BASE_URL } });
  const updateCardPhoneNumberCard = (
    cardId: string,
    phoneNumber: phoneNumberProp,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    if (!validate(cardId)) {
      setActionError({ message: 'No available card' });
      return;
    }
    updateCardPhoneNumberMutate.mutate(
      {
        data: {
          cardAccountId: cardAccountId,
          cardId: cardId,
          phoneNumber: phoneNumber
        }
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const postCardaccountsFixedLimitMutate = usePostCardaccountsFixedLimit({ axios: { baseURL: ACCESS_BASE_URL } });
  const postCardaccountsFixedLimit = (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    postCardaccountsFixedLimitMutate.mutate(
      {
        data: [
          {
            cardAccountId: cardAccountId,
            limit: limit
          }
        ]
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const cardAccounts = useMemo(() => {
    const data = queryCardAccounts.data?.data.cardAccounts;
    return data || [];
  }, [queryCardAccounts.data?.data]);

  const isLoadingCardAccounts = useMemo(() => {
    return queryCardAccounts.isLoading;
  }, [queryCardAccounts.isLoading]);

  useEffect(() => {
    setPageToken({
      nextToken: queryCards.data?.data.nextPageToken,
      preToken: queryCards.data?.data.prevPageToken
    });
  }, [queryCards.data?.data]);

  return {
    cards,
    isLoadingCards,
    reloadCards: () => queryCards.refetch(),
    nextTokenCard: pageToken.nextToken,
    preTokenCard: pageToken.preToken,
    prevCard: () => setCurrentPageToken(pageToken.preToken),
    nextCard: () => setCurrentPageToken(pageToken.nextToken),
    clearCardCurrentPageToken: () => {
      setCurrentPageToken(undefined);
    },
    lockCard,
    unlockCard,
    changeCardPin,
    setCardLimit,
    actionResponse,
    actionError,
    cardAccounts,
    isLoadingCardAccounts,
    reloadCardAccounts: () => queryCardAccounts.refetch(),
    topUpCardAccount,
    withdrawCardAccount,
    issueCard,
    activateCard,
    cancelCard,
    suspendCard,
    unsuspendCard,
    updateCardPhoneNumberCard,
    postCardaccountsFixedLimit
  };
};

export interface useCardSensitiveDetailsParams {
  cardId: string;
}

export const useCardSensitiveDetails = ({ cardId }: useCardSensitiveDetailsParams) => {
  const store = useStore();

  const queryCardSensitiveDetails = useUtxCardOverviewGetCardDetail(cardId, {
    axios: {
      headers: {
        Authorization: `Bearer ${store.authToken}`
      }
    },
    query: {
      enabled: cardId ? true : false
    }
  });

  const sensitiveDetail = useMemo(() => {
    const data = queryCardSensitiveDetails.data?.data;
    return data || [];
  }, [queryCardSensitiveDetails.data?.data]);

  return {
    sensitiveDetail
  };
};

export interface useCardTransactionParams {
  cardAccountId: string;
  pageSize?: number;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  search?: string;
  cardId?: string;
}

export const useCardTransaction = ({
  cardAccountId,
  pageSize = 10,
  dateRangeFrom,
  dateRangeTo,
  search,
  cardId
}: useCardTransactionParams) => {
  const store = useStore();

  const [currentPageToken, setCurrentPageToken] = useState<PageToken>();
  const [pageToken, setPageToken] = useState<{ nextToken?: PageToken; preToken?: PageToken }>({});
  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken ? true : false);
  }, 100);

  const queryCardTransactions = useGetCardAccountTransactions(
    {
      cardAccountId: cardAccountId,
      pageSize: pageSize,
      pageToken: currentPageToken,
      dateRangeFrom: dateRangeFrom,
      dateRangeTo: dateRangeTo,
      search: search,
      cardId: cardId
    },
    {
      axios: {
        baseURL: ACCESS_BASE_URL
      },
      query: {
        queryKey: [
          'queryCardTransactions',
          store.authToken,
          pageSize,
          currentPageToken,
          cardAccountId,
          dateRangeFrom,
          dateRangeTo,
          search,
          cardId
        ],
        retry: false,
        enabled,
        keepPreviousData: true
      }
    }
  );

  const transactions = useMemo(() => {
    const data = queryCardTransactions.data?.data.transactions;
    return data || [];
  }, [queryCardTransactions.data?.data]);

  const isLoadingCardTransactions = useMemo(() => {
    return queryCardTransactions.isLoading;
  }, [queryCardTransactions.isLoading]);

  // useEffect(() => {
  //   queryCardTransactions.refetch();
  // }, [cardAccountId, pageSize, pageToken, search]);

  useEffect(() => {
    setPageToken({ nextToken: queryCardTransactions.data?.data.nextPageToken, preToken: queryCardTransactions.data?.data.prevPageToken });
  }, [queryCardTransactions.data?.data]);

  return {
    transactions,
    isLoadingCardTransactions,
    reloadCardTransactions: () => queryCardTransactions.refetch(),
    nextToken: pageToken.nextToken,
    preToken: pageToken.preToken,
    prev: () => setCurrentPageToken(pageToken.preToken),
    next: () => setCurrentPageToken(pageToken.nextToken),
    refreshCardTransactions: () => {
      setCurrentPageToken(undefined);
      queryCardTransactions.refetch();
    },
    clearCardTransactionCurrentPageToken: () => {
      setCurrentPageToken(undefined);
    }
  };
};

export const useCountriesGetPhoneDailInList = () => {
  const queryCountriesGetPhoneDailInList = useUtxCountriesGetPhoneDailInList();
  const countriesGetPhoneDailInList = useMemo(() => {
    const data = queryCountriesGetPhoneDailInList.data?.data;
    return data || [];
  }, [queryCountriesGetPhoneDailInList.data?.data]);

  const isLoadingCountriesGetPhoneDailInList = useMemo(() => {
    return queryCountriesGetPhoneDailInList.isLoading;
  }, [queryCountriesGetPhoneDailInList.isLoading]);

  return { countriesGetPhoneDailInList, isLoadingCountriesGetPhoneDailInList };
};
